// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { Button, Empty, Modal, Tag, Typography } from "antd";
import { NumberOutlined, AppstoreOutlined } from "@ant-design/icons";
import { friendlyRoomName, type DirectoryRoom } from "../types";

// ChannelDirectory is the IRC /LIST browser: public + knockable channels and
// Spaces, with a one-click Join. Private (invite-only) channels and channels that
// belong to a Space are intentionally absent — join the Space to see those.
export function ChannelDirectory({
  open,
  rooms,
  onClose,
  onJoin,
  inline = false,
}: {
  readonly open: boolean;
  readonly rooms: DirectoryRoom[];
  readonly onClose: () => void;
  readonly onJoin: (room: DirectoryRoom) => void;
  // inline renders the body without the Modal wrapper (a mobile bottom-nav screen).
  readonly inline?: boolean;
}) {
  const body =
    rooms.length === 0 ? (
      <Empty description="No public channels yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    ) : (
      <div className="directory-list">
        {rooms.map((r) => (
          <div className="directory-item" key={r.room_id}>
            <div className="directory-info">
              <div className="directory-name">
                {r.room_type === "m.space" ? <AppstoreOutlined className="room-hash" /> : <NumberOutlined className="room-hash" />}
                {friendlyRoomName(r)}
                {r.room_type === "m.space" && <Tag color="purple">space</Tag>}
                <Tag>{r.num_joined_members}</Tag>
                {r.join_rule === "knock" && <Tag color="gold">knock</Tag>}
              </div>
              {r.topic && (
                <Typography.Text type="secondary" ellipsis>
                  {r.topic}
                </Typography.Text>
              )}
            </div>
            <Button size="small" type="primary" onClick={() => onJoin(r)}>
              Join
            </Button>
          </div>
        ))}
      </div>
    );

  if (inline) return <div className="mobile-tab-view mobile-directory">{body}</div>;
  return (
    <Modal title="Channel directory" open={open} onCancel={onClose} footer={null} width={520}>
      {body}
    </Modal>
  );
}
